import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BatteryCharging, FileText, Clock, Users2, CheckCircle2, Wrench, ArrowRight, PlusCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_ROLES, REGISTRAR_ROLES, ROLE_LABELS } from "@/lib/constants";
import api from "@/lib/apiClient";

const DASH_HERO = "https://images.pexels.com/photos/36594160/pexels-photo-36594160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function StatCard({ icon: Icon, label, value, tint, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
      <Card className="p-5 hover:-translate-y-[2px] hover:shadow-md transition-all border-border" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="flex items-center justify-between">
          <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${tint}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        <p className="mt-4 font-display text-3xl font-extrabold tracking-tight">{value ?? "—"}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
      </Card>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isSP = user?.role === "service_provider";

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = isAdmin
    ? [
        { icon: BatteryCharging, label: "Batteries", value: stats.batteries, tint: "bg-emerald-100 text-emerald-700" },
        { icon: FileText, label: "Total Claims", value: stats.claims, tint: "bg-blue-100 text-blue-700" },
        { icon: Clock, label: "Open Claims", value: stats.open_claims, tint: "bg-amber-100 text-amber-700" },
        { icon: Users2, label: "Users", value: stats.users, tint: "bg-violet-100 text-violet-700" },
      ]
    : isSP
    ? [
        { icon: Wrench, label: "Assigned", value: stats.assigned, tint: "bg-blue-100 text-blue-700" },
        { icon: Clock, label: "Pending", value: stats.pending, tint: "bg-amber-100 text-amber-700" },
        { icon: CheckCircle2, label: "Completed", value: stats.completed, tint: "bg-emerald-100 text-emerald-700" },
        { icon: BatteryCharging, label: "Centers", value: stats.centers, tint: "bg-violet-100 text-violet-700" },
      ]
    : [
        { icon: BatteryCharging, label: "My Batteries", value: stats.batteries, tint: "bg-emerald-100 text-emerald-700" },
        { icon: FileText, label: "My Claims", value: stats.claims, tint: "bg-blue-100 text-blue-700" },
        { icon: Clock, label: "Open Claims", value: stats.open_claims, tint: "bg-amber-100 text-amber-700" },
        { icon: CheckCircle2, label: "Resolved", value: stats.resolved, tint: "bg-indigo-100 text-indigo-700" },
      ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero */}
      <Card className="relative overflow-hidden border-none">
        <img src={DASH_HERO} alt="Industrial batteries" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#052e21]/85" />
        <div className="relative z-10 p-8 text-white">
          <p className="text-emerald-300 text-sm font-semibold uppercase tracking-widest">{ROLE_LABELS[user?.role]}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">Welcome, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-2 text-emerald-100/80 max-w-lg">
            Manage battery registrations, warranty claims and resolutions across the Greenrgy network.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/claims/new")} className="bg-emerald-500 hover:bg-emerald-600 gap-2" data-testid="hero-new-claim-button">
              <PlusCircle className="h-4 w-4" /> Raise a Claim
            </Button>
            {REGISTRAR_ROLES.includes(user?.role) && (
              <Button onClick={() => navigate("/register-battery")} variant="secondary" className="gap-2" data-testid="hero-register-button">
                <BatteryCharging className="h-4 w-4" /> Register Battery
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => <StatCard key={c.label} {...c} i={i} />)}
      </div>

      {/* Quick links */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate("/claims")} data-testid="quick-claims">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display font-bold text-lg">Warranty Claims</h3>
              <p className="text-sm text-muted-foreground mt-1">Track and manage all warranty claim requests and resolutions.</p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
          </div>
        </Card>
        <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate(isAdmin ? "/reports" : "/claims/new")} data-testid="quick-secondary">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display font-bold text-lg">{isAdmin ? "Reports & Analytics" : "Raise New Claim"}</h3>
              <p className="text-sm text-muted-foreground mt-1">{isAdmin ? "View claim trends, TAT and resolution breakdowns." : "Start a new warranty claim for a registered battery."}</p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
          </div>
        </Card>
      </div>
    </div>
  );
}
